/**
 * RHDiagnostico V55 — 4 abas: Visão Geral, Temporal, Liderança, Risco por Porte
 */
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { KMinBanner, KMinGuard, QualifierChart } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ShieldAlert, TrendingUp, Users, Building2, ChevronRight } from "lucide-react";
import { useState, useMemo } from "react";
import { Link } from "wouter";
import { HeatmapGrid, RadarChart, InfoTooltip, IndicatorManualModal } from "@/components/home";

type Tab = "overview" | "temporal" | "leadership" | "riskBySize";

export default function RHDiagnostico() {
  usePageTitle("Diagnóstico RH");
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("overview");

  // Original diagnostic data
  const { data, isLoading, error } = trpc.dashboards.rh.diagnostic.useQuery(undefined, {
    enabled: !!user && user.role === "client_rh",
    refetchOnWindowFocus: false,
  });

  // Expanded diagnostic data
  const { data: expanded } = trpc.dashboardsAdv.rh.diagnosticExpanded.useQuery(undefined, {
    enabled: !!user && user.role === "client_rh",
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Diagnóstico">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "overview", label: "Visão Geral", icon: ShieldAlert },
    { key: "temporal", label: "Temporal", icon: TrendingUp },
    { key: "leadership", label: "Liderança", icon: Users },
    { key: "riskBySize", label: "Risco por Porte", icon: Building2 },
  ];

  return (
    <SalviaLayout title="Dashboard — Diagnóstico">
      <div className="space-y-6">
        {data?.kMin && <KMinBanner kMin={data.kMin} />}

        {/* Tab bar */}
        <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.key ? "bg-white shadow text-[#002F59]" : "text-muted-foreground hover:text-foreground"}`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === "overview" && <OverviewTab data={data} expanded={expanded} loading={loading} />}
        {tab === "temporal" && <TemporalTab expanded={expanded} />}
        {tab === "leadership" && <LeadershipTab expanded={expanded} />}
        {tab === "riskBySize" && <RiskBySizeTab expanded={expanded} />}
      </div>
      <IndicatorManualModal profile="rh" />
    </SalviaLayout>
  );
}

function OverviewTab({ data, expanded, loading }: { data: any; expanded: any; loading: boolean }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QualifierChart data={data?.qualifierDistribution ?? []} title="Qualificadores" loading={loading} />
        <QualifierChart data={data?.classificationDistribution ?? []} title="Classificação de Risco (P×S)" loading={loading} />
      </div>

      {/* Heatmap */}
      {expanded?.heatmap && expanded.heatmap.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-semibold">Heatmap: Setor × Cargo</h3>
            <InfoTooltip label="Score" formula="Score Ergos = Σ(respostas × peso)" description="Quanto mais escuro, maior o risco" />
          </div>
          <HeatmapGrid data={expanded.heatmap} />
        </Card>
      )}

      {/* Ranking de Fatores */}
      {expanded?.factorRanking && expanded.factorRanking.length > 0 && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-[#002F59] mb-4">Ranking de Fatores de Risco</h3>
          <div className="space-y-2">
            {expanded.factorRanking.map((f: any, i: number) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs font-mono text-muted-foreground w-6">{i + 1}.</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{f.factor}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{f.avgScore?.toFixed(1)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500" style={{ width: `${Math.min((f.avgScore / 25) * 100, 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Radar Chart */}
      {expanded?.radarData && expanded.radarData.length >= 3 && (
        <Card className="p-6">
          <RadarChart data={expanded.radarData} title="Radar de Dimensões" />
        </Card>
      )}

      {/* Detail table with drill-down */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold mb-4">Diagnóstico por Setor e Cargo</h3>
        {loading ? (
          <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (data?.details ?? []).length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Nenhum resultado disponível</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Setor</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Cargo</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Qualificador</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Classificação</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Respondentes</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">kMin</th>
                  <th className="px-3 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {data!.details.map((d: any, i: number) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2.5 font-medium">{d.sector}</td>
                    <td className="px-3 py-2.5">{d.jobTitle}</td>
                    <td className="px-3 py-2.5">
                      <KMinGuard meetsKMin={d.meetsKMin} kMin={data!.kMin} respondentCount={d.respondentCount}>
                        <span className="font-medium">{d.qualifier || "—"}</span>
                      </KMinGuard>
                    </td>
                    <td className="px-3 py-2.5">
                      <KMinGuard meetsKMin={d.meetsKMin} kMin={data!.kMin} respondentCount={d.respondentCount}>
                        <span className="font-medium">{d.classification || "—"}</span>
                      </KMinGuard>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{d.respondentCount}</td>
                    <td className="px-3 py-2.5">
                      {d.meetsKMin ? (
                        <span className="text-emerald-600 text-xs font-medium">OK</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                          <ShieldAlert className="h-3 w-3" /> Protegido
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {d.evaluationId && (
                        <Link href={`/dashboards/rh/diagnostico/${d.evaluationId}`}>
                          <ChevronRight size={16} className="text-muted-foreground hover:text-foreground cursor-pointer" />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function TemporalTab({ expanded }: { expanded: any }) {
  if (!expanded?.temporalEvolution || expanded.temporalEvolution.length === 0) {
    return <Card className="p-8 text-center text-muted-foreground">Dados temporais não disponíveis ainda. Necessário mais de uma avaliação.</Card>;
  }

  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold text-[#002F59] mb-4">Evolução Temporal do Score</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Período</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Score Médio</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Avaliações</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Tendência</th>
            </tr>
          </thead>
          <tbody>
            {expanded.temporalEvolution.map((t: any, i: number) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-3 py-2.5 font-medium">{t.period}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{t.avgScore?.toFixed(1)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{t.count}</td>
                <td className="px-3 py-2.5">
                  {i > 0 && (
                    <span className={t.avgScore > expanded.temporalEvolution[i-1].avgScore ? "text-red-600" : "text-emerald-600"}>
                      {t.avgScore > expanded.temporalEvolution[i-1].avgScore ? "↑ Piorou" : "↓ Melhorou"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function LeadershipTab({ expanded }: { expanded: any }) {
  if (!expanded?.leadershipScores || expanded.leadershipScores.length === 0) {
    return <Card className="p-8 text-center text-muted-foreground">Dados de liderança não disponíveis. Nenhum cargo de gestão encontrado.</Card>;
  }

  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold text-[#002F59] mb-4">Score por Cargo de Gestão</h3>
      <div className="space-y-3">
        {expanded.leadershipScores.map((l: any, i: number) => (
          <div key={i} className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{l.jobTitle}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{l.respondentCount} resp.</span>
                  <span className="text-sm font-semibold tabular-nums">{l.avgScore?.toFixed(1)}</span>
                </div>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min((l.avgScore / 25) * 100, 100)}%`,
                    backgroundColor: l.avgScore >= 15 ? "#ef4444" : l.avgScore >= 10 ? "#f59e0b" : "#22c55e",
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RiskBySizeTab({ expanded }: { expanded: any }) {
  if (!expanded?.riskBySize || expanded.riskBySize.length === 0) {
    return <Card className="p-8 text-center text-muted-foreground">Dados de risco por porte não disponíveis.</Card>;
  }

  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold text-[#002F59] mb-4">Risco por Porte da Empresa</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Porte</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Empresas</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Score Médio</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Nível</th>
            </tr>
          </thead>
          <tbody>
            {expanded.riskBySize.map((r: any, i: number) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-3 py-2.5 font-medium">{r.sizeLabel}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.companyCount}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.avgScore?.toFixed(1)}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${r.avgScore >= 15 ? "bg-red-100 text-red-700" : r.avgScore >= 10 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {r.avgScore >= 15 ? "Alto" : r.avgScore >= 10 ? "Médio" : "Baixo"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
